import { useRailbar } from ".";
import type { ReactionType } from "@/react/components/Reactions/Reaction";
import type { ReactionAttributes } from "@/react/types/Chat/ChatReaction";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import { useChatRoomStore } from "./store";
import { useScrollInfo } from "./useScrollInfo";

interface WebsocketEvent {
  event: "createReaction" | "destroyReaction";
  json_message: {
    community_member_id: number;
    emoji: string;
    reactionable_id: number;
    reactionable_type: string;
  };
}

export const useChatMessageReaction = () => {
  const {
    chatRoomMessages = [],
    setChatRoomMessages,
    setScrollInfo,
  } = useChatRoomStore();

  const { calculateScrollInfo } = useScrollInfo();
  const { parentMessage, updateParentMessage } = useRailbar();

  const onReactionWebsocketEvent = (data: WebsocketEvent): void => {
    const { event } = data;
    switch (event) {
      case "createReaction":
      case "destroyReaction":
        handleReactionFromWebsocket(data);
        break;
      default:
        break;
    }
  };

  const createReactionFromMessage = (
    reactionAttributes: ReactionAttributes,
  ): void => {
    const { communityMemberId, emoji, reactionableId } = reactionAttributes;
    const updatedChatRoomMessages: ChatRoomMessage[] =
      structuredClone(chatRoomMessages);

    const chatRoomMessageIndex: number = updatedChatRoomMessages.findIndex(
      (chatRoomMessage: ChatRoomMessage) =>
        chatRoomMessage.id === reactionableId,
    );

    if (chatRoomMessageIndex === -1) return;

    const message = updatedChatRoomMessages[chatRoomMessageIndex];
    const existingReactionIndex = message.reactions.findIndex(
      (reaction: ReactionType) => reaction.emoji === emoji,
    );

    if (
      existingReactionIndex !== -1 &&
      message.reactions[existingReactionIndex]?.community_member_ids?.includes(
        communityMemberId,
      )
    ) {
      return;
    }

    if (existingReactionIndex !== -1) {
      message.reactions[existingReactionIndex].community_member_ids.push(
        communityMemberId,
      );
      message.reactions[existingReactionIndex].count += 1;
    } else {
      const newReaction: ReactionType = {
        emoji,
        community_member_ids: [communityMemberId],
        count: 1,
      };
      message.reactions.push(newReaction);
    }

    setScrollInfo(calculateScrollInfo({ event: "reaction" }));
    setChatRoomMessages(updatedChatRoomMessages);

    if (parentMessage?.id === reactionableId) {
      const parentMessageCopy = updatedChatRoomMessages.find(
        (message: ChatRoomMessage) => message.id === parentMessage.id,
      );
      if (parentMessageCopy) {
        updateParentMessage({ ...parentMessageCopy });
      }
    }
  };

  const removeReactionFromMessage = (
    reactionAttributes: ReactionAttributes,
  ): void => {
    const { communityMemberId, emoji, reactionableId } = reactionAttributes;
    const updatedChatRoomMessages: ChatRoomMessage[] =
      structuredClone(chatRoomMessages);

    const chatRoomMessageIndex = updatedChatRoomMessages.findIndex(
      (chatRoomMessage: ChatRoomMessage) =>
        chatRoomMessage.id === reactionableId,
    );

    if (chatRoomMessageIndex === -1) return;

    const message = updatedChatRoomMessages[chatRoomMessageIndex];
    const existingReactionIndex = message.reactions.findIndex(
      (reaction: ReactionType) => reaction.emoji === emoji,
    );

    if (
      existingReactionIndex === -1 ||
      !message.reactions[existingReactionIndex]?.community_member_ids?.includes(
        communityMemberId,
      )
    ) {
      return;
    }

    if (message.reactions[existingReactionIndex].count === 1) {
      message.reactions.splice(existingReactionIndex, 1);
    } else {
      message.reactions[existingReactionIndex].community_member_ids =
        message.reactions[existingReactionIndex].community_member_ids.filter(
          (id: number) => id !== communityMemberId,
        );
      message.reactions[existingReactionIndex].count -= 1;
    }

    setScrollInfo(calculateScrollInfo({ event: "reaction" }));
    setChatRoomMessages(updatedChatRoomMessages);

    if (parentMessage?.id === reactionableId) {
      const parentMessageCopy = updatedChatRoomMessages.find(
        (message: ChatRoomMessage) => message.id === parentMessage.id,
      );
      if (parentMessageCopy) {
        updateParentMessage({ ...parentMessageCopy });
      }
    }
  };

  const handleReactionFromWebsocket = (data: WebsocketEvent): void => {
    if (data.json_message.reactionable_type === "ChatRoomMessage") {
      const {
        community_member_id: communityMemberId,
        emoji,
        reactionable_id: reactionableId,
      } = data.json_message;

      const reactionAttributes: ReactionAttributes = {
        communityMemberId,
        emoji,
        reactionableId,
      };

      if (data.event === "createReaction") {
        createReactionFromMessage(reactionAttributes);
      } else if (data.event === "destroyReaction") {
        removeReactionFromMessage(reactionAttributes);
      }
    }
  };

  return {
    createReactionFromMessage,
    removeReactionFromMessage,
    handleReactionFromWebsocket,
    onReactionWebsocketEvent,
  };
};
